/**
 * Modal informativo para solicitar permissão de localização
 * @module components/Ponto/LocationPermissionModal
 */

import React from 'react';
import { MapPin, Shield, CheckCircle } from 'lucide-react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';

/**
 * Modal que informa o usuário sobre o uso da localização
 * @param {boolean} isOpen - Se o modal está aberto
 * @param {function} onClose - Função para fechar o modal
 * @param {function} onConfirm - Função chamada quando o usuário confirma e permite localização
 * @param {function} onSkip - Função chamada quando o usuário escolhe continuar sem localização
 * @param {string} actionType - Tipo de ação ('checkin' ou 'checkout')
 */
const LocationPermissionModal = ({ 
    isOpen, 
    onClose, 
    onConfirm,
    onSkip,
    actionType = 'checkin' 
}) => {
    const actionText = actionType === 'checkin' ? 'Check-in' : 'Check-out';
    
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title=""
            size="md"
            hideHeader={true}
        >
            <div className="flex flex-col items-center text-center p-2">
                {/* Ícone de localização */}
                <div className="relative mb-4">
                    <div className="absolute inset-0 bg-teal-100 rounded-full animate-ping opacity-75"></div>
                    <MapPin className="relative h-12 w-12 text-teal-600" />
                </div>

                {/* Título */}
                <h2 className="text-xl font-bold text-gray-800 mb-3">
                    Acesso à Localização
                </h2>

                {/* Mensagem explicativa */}
                <div className="text-gray-600 text-base leading-relaxed mb-6 space-y-3">
                    <p className="text-center">
                        Para registrar o <strong className="text-gray-900">{actionText}</strong> com maior precisão, 
                        gostaríamos de acessar sua localização atual.
                    </p>
                    
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-left space-y-3">
                        <div className="flex items-start gap-3">
                            <Shield className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-teal-900 mb-1">Por que solicitamos sua localização?</p>
                                <p className="text-sm text-teal-800 leading-relaxed">
                                    Sua localização é utilizada exclusivamente para <strong>auditoria e segurança</strong> dos atendimentos, 
                                    garantindo que o registro seja feito no local correto e proporcionando maior rastreabilidade 
                                    dos serviços prestados.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-start gap-3 text-sm text-gray-700 bg-green-50 border border-green-200 rounded-lg p-3">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-green-900 mb-1">Sua privacidade é importante</p>
                            <p className="text-green-800 leading-relaxed">
                                Você pode continuar normalmente mesmo sem permitir o acesso à localização. 
                                O <strong>{actionText}</strong> será registrado com sucesso de qualquer forma.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Botões de ação */}
                <div className="flex flex-col sm:flex-row justify-center gap-3 w-full">
                    <PrimaryButton
                        onClick={onSkip || onClose}
                        className="!bg-gray-200 !text-gray-700 hover:!bg-gray-300 !shadow-none w-full sm:w-auto"
                    >
                        Continuar sem Localização
                    </PrimaryButton>
                    
                    <PrimaryButton
                        onClick={onConfirm}
                        className="!bg-teal-600 hover:!bg-teal-700 !text-white !shadow-sm hover:!shadow-md transition-all duration-200 w-full sm:w-auto"
                    >
                        Permitir e Continuar
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default LocationPermissionModal;

